import React, { useState } from "react";
import "./accumulatorCardDesign.css";
import { AccumulatorResult } from "../../../../@types/eligibilityCheck.types";

interface ProgressRowProps {
	label: string;
	spent: number;
	total: number | "Unlimited";
}

const ProgressRow: React.FC<ProgressRowProps> = ({ label, spent, total }) => {
	const isUnlimited = total === "Unlimited";

	// Safety calculations for numeric totals vs unlimited
	const remaining = isUnlimited ? 0 : Math.max(0, total - spent);
	const percentage = isUnlimited ? 0 : Math.min(100, Math.max(0, (spent / total) * 100));

	return (
		<div className={`accumulator-metric ${isUnlimited ? "unlimited-metric" : ""}`}>
			<div className="metric-header">
				<span className="metric-title">{label}</span>
				<span className="metric-values">
					<strong>${spent.toFixed(2)}</strong> /{" "}
					<span className="total-limit">
						{isUnlimited ? <span className="infinity-symbol">No Limit (∞)</span> : `$${total.toFixed(2)}`}
					</span>
				</span>
			</div>

			<div className="progress-bar-container">
				<div
					className={`progress-bar-fill ${isUnlimited ? "unlimited-fill" : ""}`}
					style={{ width: `${percentage}%` }}
					role="progressbar"
					aria-valuenow={spent}
					aria-valuemin={0}
					aria-valuemax={isUnlimited ? undefined : total}
				/>
			</div>

			<div className="metric-footer">
				{isUnlimited ? (
					<>
						<span className="badge unlimited-badge">No Cap Applied</span>
						<span className="percentage-text">Unlimited Coverage</span>
					</>
				) : (
					<>
						<span className="badge remaining-badge">${remaining?.toFixed(2)} Remaining</span>
						<span className="percentage-text">{percentage.toFixed(0)}% Used</span>
					</>
				)}
			</div>
		</div>
	);
};

const AccumulatorCard: React.FC<{ accumulator: AccumulatorResult }> = ({ accumulator }): JSX.Element => {
	const [activeMainTab, setActiveMainTab] = useState<"inNetwork" | "outOfNetwork">("inNetwork");

	const hasNetworkData =
		accumulator.NETWORK_DEDUCTIBLE !== undefined || accumulator.NETWORK_OUTOFPOCKET !== undefined;

	const INDIVIDUAL_DEDUCTIBLE = 1500;
	const INDIVIDUAL_OUT_OF_POCKET = 9100;

	const OUT_OF_NETWORK_DEDUCTIBLE = 3000;
	// const OUT_OF_NETWORK_OUT_OF_POCKET = "Infinity";

	const parseVal = (val?: string | number) => {
		const parsed = parseFloat(String(val ?? "0"));
		return isNaN(parsed) ? 0 : parsed;
	};

	// Standard card fallback when network details are unavailable
	if (!hasNetworkData) {
		return (
			<div className="accumulator-wrapper">
				<div className="accumulator-card">
					<div className="card-header">
						<h2>Coverage Overview</h2>
						<span className="status-tag">Plan Default</span>
					</div>

					<div className="metrics-list">
						<ProgressRow
							label="Deductible"
							spent={parseVal(String(accumulator.PLAN_DEDUCTIBLE))}
							total={3500}
						/>
						<ProgressRow
							label="Out of Pocket Maximum"
							spent={parseVal(String(accumulator.PLAN_OUTOFPOCKET))}
							total={7500}
						/>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="accumulator-wrapper">
			<div className="accumulator-card">
				{/* SEGMENTED TAB SWITCHER */}
				<div className="segmented-control" role="tablist">
					<button
						type="button"
						role="tab"
						aria-selected={activeMainTab === "inNetwork"}
						className={`segmented-btn ${activeMainTab === "inNetwork" ? "active" : ""}`}
						onClick={() => setActiveMainTab("inNetwork")}
					>
						<svg
							className="tab-icon"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
						>
							<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
							<line x1="12" y1="8" x2="12" y2="14" />
							<line x1="9" y1="11" x2="15" y2="11" />
						</svg>
						In-Network
					</button>
					<button
						type="button"
						role="tab"
						aria-selected={activeMainTab === "outOfNetwork"}
						className={`segmented-btn ${activeMainTab === "outOfNetwork" ? "active" : ""}`}
						onClick={() => setActiveMainTab("outOfNetwork")}
					>
						<svg className="tab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
							<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
						</svg>
						Out-of-Network
					</button>
				</div>

				{/* TAB CONTENT: IN-NETWORK */}
				{activeMainTab === "inNetwork" && (
					<div className="tab-panel animate-fade-in" role="tabpanel">
						<div className="section-title">
							<h3>In-Network Accumulators</h3>
							<p className="section-subtitle">Deductible and maximum out-of-pocket tracking</p>
						</div>

						<div className="metrics-list">
							<ProgressRow
								label="Deductible"
								spent={parseVal(String(accumulator.NETWORK_DEDUCTIBLE))}
								total={INDIVIDUAL_DEDUCTIBLE}
							/>
							<ProgressRow
								label="Out of Pocket Maximum"
								spent={parseVal(String(accumulator.NETWORK_OUTOFPOCKET))}
								total={INDIVIDUAL_OUT_OF_POCKET}
							/>
						</div>
					</div>
				)}

				{/* TAB CONTENT: OUT-OF-NETWORK */}
				{activeMainTab === "outOfNetwork" && (
					<div className="tab-panel animate-fade-in" role="tabpanel">
						<div className="section-title">
							<h3>Out-of-Network Accumulators</h3>
							<p className="section-subtitle">Real-time deductible and maximum out-of-pocket tracking</p>
						</div>
						<div className="metrics-list">
							<ProgressRow
								label="Deductible"
								spent={parseVal(String(accumulator.PLAN_DEDUCTIBLE))}
								total={OUT_OF_NETWORK_DEDUCTIBLE}
							/>
							<ProgressRow
								label="Out of Pocket Maximum"
								spent={parseVal(String(accumulator.PLAN_OUTOFPOCKET))}
								total={"Unlimited"}
							/>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default AccumulatorCard;
